import React from 'react';
import { CashieringViewFormValues } from '../data/useCashieringView';
import { TaxWithholdingType } from '@tradingblock/types';

interface TaxWithholdingRequirementProps {
  values: Partial<CashieringViewFormValues>;
  type?: 'federal' | 'state';
  withholdingOption?: 'Disabled' | 'Voluntary' | 'Mandatory';
  requiredMinimum?: 'NoMinimum' | 'Percentage' | 'PercentageOfFederal';
  requiredMinimumPercent?: number;
}

export const TaxWithholdingRequirement: React.FC<TaxWithholdingRequirementProps> = ({
  values,
  type,
  withholdingOption,
  requiredMinimum,
  requiredMinimumPercent,
}) => {
  // Handle the 'Disabled' withholding option by displaying a specific message.
  if (withholdingOption === 'Disabled') {
    return (
      <p className="txt-sm">
        Based on your state of residence, you are under the 'Disabled' withholding category for IRA distributions at the{' '}
        {type} level. This means that you are not required and are unable to withhold any {type} taxes from your
        distribution.
      </p>
    );
  }

  // If any required prop is missing, return null to avoid rendering incomplete information.
  if (!type || !withholdingOption || !requiredMinimum) {
    return null;
  }

  // Calculate the amount value from the provided form values.
  const amountValue = getAmountValue(values) || 0;
  if (amountValue === 0) {
    return null;
  }

  // Calculate the percentage of federal withholding if applicable.
  const percentageOfFederal = calculatePercentageOfFederal(
    amountValue,
    requiredMinimum,
    requiredMinimumPercent,
    values
  );

  // Render the appropriate withholding message based on the provided props.
  return (
    <>
      {renderWithholdingMessage(
        withholdingOption,
        requiredMinimum,
        type,
        requiredMinimumPercent,
        amountValue,
        percentageOfFederal,
        values
      )}
    </>
  );
};

// Helper function to extract and parse the amount value from the form values.
const getAmountValue = (values: Partial<CashieringViewFormValues>): number => {
  const { transfer } = values;
  const { amount } = transfer || {};

  if (!amount) {
    return 0;
  }

  // If the amount is a string, remove any non-numeric characters and convert it to a number.
  return typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.]/g, '')) : amount;
};

// Helper function to calculate the percentage of federal withholding if required.
export const calculatePercentageOfFederal = (
  amountValue: number,
  requiredMinimum?: 'NoMinimum' | 'Percentage' | 'PercentageOfFederal',
  requiredMinimumPercent?: number,
  values?: Partial<CashieringViewFormValues>
) => {
  // Check if the required minimum is 'PercentageOfFederal' and federal tax withholding information is available.
  if (
    requiredMinimum === 'PercentageOfFederal' &&
    values &&
    values.transfer &&
    values.transfer.iraDistribution &&
    values.transfer.iraDistribution.federalTaxWithholding
  ) {
    const { federalTaxWithholding } = values.transfer.iraDistribution;
    if (federalTaxWithholding.valueType === TaxWithholdingType.Percent) {
      // Calculate the federal amount based on the withholding percentage and amount value.
      const federalAmount = ((federalTaxWithholding.value || 0) / 100) * amountValue;
      const percentageOfFederalValue = (requiredMinimumPercent || 0) * federalAmount;
      const percentageOfFederalPercentValue = (percentageOfFederalValue / amountValue) * 100;
      return { percentageOfFederalValue, percentageOfFederalPercentValue };
    }
    if (federalTaxWithholding.valueType === TaxWithholdingType.Fixed) {
      // Calculate the federal amount based on the withholding fixed value.
      const federalAmount = federalTaxWithholding.value || 0;
      const percentageOfFederalValue = (requiredMinimumPercent || 0) * federalAmount;
      const percentageOfFederalPercentValue = (percentageOfFederalValue / amountValue) * 100;
      return { percentageOfFederalValue, percentageOfFederalPercentValue };
    }
  }
  return { percentageOfFederalValue: 0, percentageOfFederalPercentValue: 0 };
};

// Helper function to render the appropriate withholding message based on the provided props.
const renderWithholdingMessage = (
  withholdingOption: string,
  requiredMinimum: string,
  type: string,
  requiredMinimumPercent: number = 0,
  amountValue: number,
  percentageOfFederal: { percentageOfFederalValue: number; percentageOfFederalPercentValue: number },
  values?: Partial<CashieringViewFormValues>
) => {
  const { percentageOfFederalValue, percentageOfFederalPercentValue } = percentageOfFederal;

  switch (withholdingOption) {
    case 'Voluntary':
      if (requiredMinimum === 'NoMinimum') {
        // Render message for voluntary withholding with no minimum requirement.
        return (
          <p className="pos txt-sm">
            Based on your state of residence, you are under the 'Voluntary' withholding category for IRA distributions
            at the {type} level. This means you may specify any withholding amount, including opting for 0%, giving you
            flexibility in managing your {type} tax obligations.
          </p>
        );
      } else if (requiredMinimum === 'PercentageOfFederal' && type === 'state') {
        // Render message for voluntary withholding based on a percentage of federal withholding.
        return (
          <p className="warn txt-sm">
            Based on your state of residence, state tax withholding is mandatory only if federal tax withholding is
            present. If you elect not to withhold federal taxes, you are not required to withhold state taxes from your
            distribution. If you do withhold federal taxes, state tax withholding becomes mandatory and must be a
            minimum of <strong>{(requiredMinimumPercent * 100).toFixed(4)}%</strong> of the federal tax withholding
            amount. This amounts to approximately{' '}
            <strong>
              {percentageOfFederalValue.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}{' '}
              ({percentageOfFederalPercentValue.toFixed(4)}%){' '}
            </strong>
            .
          </p>
        );
      } else if (requiredMinimum === 'Percentage') {
        // Render a message informing the user that the state tax withholding is voluntary but if it is present, it must meet the minimum percentage requirement.
        return (
          <p className="warn txt-sm">
            Based on your state of residence, {type} tax withholding is voluntary but if it is present, it must meet the
            minimum percentage requirement of <strong>{(requiredMinimumPercent * 100).toFixed(4)}%</strong> of the
            distribution amount. This amounts to approximately{' '}
            <strong>
              {(amountValue * requiredMinimumPercent).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </strong>
            .
          </p>
        );
      }
      break;
    case 'Mandatory':
      // Render message for mandatory withholding with a required minimum percentage.
      if (requiredMinimum !== 'PercentageOfFederal') {
        return (
          <p className="warn txt-sm">
            Your state of residence mandates 'Mandatory' withholding on IRA distributions. This enforces a required
            minimum withholding percentage of <strong>{(requiredMinimumPercent * 100).toFixed(4)}%</strong> on your
            distribution, ensuring compliance with state tax regulations. This amounts to approximately{' '}
            <strong>
              {(amountValue * requiredMinimumPercent).toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </strong>
            .
          </p>
        );
      }

      if (requiredMinimum === 'PercentageOfFederal') {
        // if the federal tax withholding is not mandatory, display a message informing the user that the state tax withholding requirement only becomes mandatory if a federal tax withholding amount is present.
        return (
          <p className="warn txt-sm">
            Based on your state of residence, state tax withholding is mandatory only if federal tax withholding is
            present. If you elect not to withhold federal taxes, you are not required to withhold state taxes from your
            distribution. If you do withhold federal taxes, state tax withholding becomes mandatory and must be a
            minimum of <strong>{(requiredMinimumPercent * 100).toFixed(4)}%</strong> of the federal tax withholding
            amount. This amounts to approximately{' '}
            <strong>
              {percentageOfFederalValue.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}{' '}
              ({percentageOfFederalPercentValue.toFixed(4)}%){' '}
            </strong>
            .
          </p>
        );
      }
    default:
      return null;
  }
};
